<template>
  <div
    class="inputs-radio-button-centered"
    :class="{'checked': isChecked}"
    @click="updateValue"
  >
    <input
      type="radio"
      :id="id"
      :name="radioGroupName || name"
      :value="value"
      :checked="isChecked"
      :disabled="disabled"
      @change="updateValue"
      @focus="focusState = true"
      @blur="focusState = false"
      class="inputs-radio-button-centered__input"
      :aria-labelledby="`label-${id}`"
    >
    <span class="inputs-radio-button-centered__button" />
    <div
      v-if="imgUrl"
      class="inputs-radio-button-centered__img-container"
    >
      <img loading="lazy"
        :src="imgUrl"
        alt=""
        class="inputs-radio-button-centered__img"
      >
    </div>
    <label
      :for="id"
      :id="`label-${id}`"
      class="inputs-radio-button-centered__label"
    >
      <span
        class="inputs-radio-button-centered__text"
        v-html="label"
      />
    </label>
  </div>
</template>
<script lang="ts" setup>
const props = defineProps({
  value: {
    type: [String, Number, Boolean],
    required: true
  },
  modelValue: {
    type: [String, Number, Boolean],
    default: null
  },
  name: {
    type: String,
    default: 'radio-button-group'
  },
  label: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  id: {
    type: String,
    default: ''
  },
  imgUrl: {
    type: String,
    default: ''
  }
})
const emits = defineEmits(['update:modelValue'])

// Generate local ID only if not passed as prop
const generatedId = `radio-${Math.random().toString(36).substring(2, 9)}`
const id = computed(() => props.id || generatedId)

// injects from parent RadioGroup
const radioGroupName = inject<string | null>('radioGroupName', null)
const radioGroupModelValue = inject<Ref<any> | null>('radioGroupModelValue', null)
// eslint-disable-next-line no-unused-vars
const updateRadioValue = inject<((value: any) => void) | null>('updateRadioValue', null)

const focusState = ref(false)

const isChecked = computed(() => {
  if (radioGroupModelValue !== null) {
    return radioGroupModelValue.value === props.value
  }
  return props.modelValue === props.value
})

// Update value on change
const updateValue = ($event: MouseEvent | Event) => {
  if (props.disabled) {
    return
  }
  if ($event.type === 'change') {
    $event.preventDefault()
  }

  // tries to call updateRadioValue from the parent RadioGroup
  if (updateRadioValue) {
    updateRadioValue(props.value)
  } else {
    // standalone radio button
    emits('update:modelValue', props.value)
  }
}
</script>

<style lang="scss">
.inputs-radio-button-centered {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  padding: 1rem;

  &:hover {
    background-color: $color__fst__light__gray;
  }

  &.focus {
    @include breakpoint-up($sm) {
      box-shadow: 0 0 0 .125rem $color__black;
    }
  }

  &__input {
    position: absolute;
    width: 0;
    opacity: 0;
  }

  input[type='radio'] {
    display: none;
  }

  &__button {
    position: relative;
    display: flex;
    align-items: center;
    border-radius: 50%;
    width: .5rem;
    height: .5rem;
    padding: .25rem;
    flex-shrink: 0;
  }

  &:not(.checked) {
    border: .0625rem solid $color__fst__gray__030;

    .inputs-radio-button-centered__button {
      border: .0625rem solid $color__fst__primary;
      background-color: $color__content--background__secondary;
    }
  }

  &.checked {
    border: .125rem solid $color__fst__primary;
    z-index: 1;

    .inputs-radio-button-centered__button {
      border: .0625rem solid $color__fst__primary;
      background-color: $color__white;

      &::after {
        content: '';
        position: absolute;
        top: .25rem;
        left: .25rem;
        width: .5rem;
        height: .5rem;
        border-radius: 50%;
        background-color: $color__fst__primary;
      }
    }
  }

  &__label {
    display: inline-block;
    font-size: .875rem;
    user-select: none;
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-weight: $base-text-font-weight-light;
  }

  &__img-container {
    width: 5.25rem; /* 84px converted to rem */
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  }

  &__img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.inputs-radio-button-centered:has(input:focus-visible) {
  box-shadow: 0 0 0 .125rem $color__black;
}
</style>
